/**
 * Experiment registry — single source of truth for all A/B tests.
 *
 * Every experiment must be registered here before implementation.
 * The PostHog experiment UI must use the same flag key.
 *
 * Usage:
 *   import { EXPERIMENTS } from "@/lib/experiments";
 *   const config = EXPERIMENTS["hero-cta-copy"];
 */

export interface ExperimentConfig {
  /** PostHog feature flag key (must match the experiment in PostHog UI) */
  flag: string
  /** All variant names including "control" */
  variants: readonly string[]
  /** Human-readable description of what's being tested */
  description: string
  /** Which pages this experiment runs on (for documentation) */
  pages: string[]
  /** Primary metric event name */
  metric: string
  /** Whether this experiment needs server-side bootstrapping (page-level tests) */
  serverSide?: boolean
}

/**
 * Active experiments. Commented-out entries are concluded experiments
 * kept for reference. Remove the code + registry entry once cleanup is done.
 *
 * To add a new experiment:
 * 1. Add entry here
 * 2. Create experiment in PostHog UI with same flag key
 * 3. Implement using <Experiment> component or useExperimentVariant()
 * 4. Add unit tests for each variant
 */
export const EXPERIMENTS = {
  "homepage-hero-v1": {
    flag: "homepage-hero-v1",
    variants: ["control", "test"],
    description:
      "Test pricing pill above hero heading + 'Find your test' secondary CTA linking to get.aheadhealth.com",
    pages: ["homepage"],
    metric: "cta_click",
  },

  // homepage-membership-first-v1 (AHD-6700) concluded on the membership-first
  // arm, which shipped as the permanent positioning for CH. The A/B
  // plumbing (flag, precompute entry, edge assignment, exposure, no-flash
  // <head> script + data-mf CSS) is gone; what varies now is MARKET, not user,
  // so both render paths live behind the country gate
  // `feature:membership-first-positioning` (lib/country-gates.ts) — NL, DE
  // and AT keep the testing-first copy. PostHog flag archived
  // separately.

  // homepage-membership-section-v1 concluded on membership-section, which
  // shipped as the permanent CH homepage layout: the dedicated
  // HomepageMembershipSection now renders unconditionally directly under the
  // value-prop cards (above 'Why early prevention matters') for membership
  // markets — the `isMembershipFirst` branch inside the ch-only
  // `section:home.membership-banner` gate in homepage-client.tsx. The block was
  // dropped unmeasured in PR #2104 and won on its own when re-run. The slot
  // component, its edge-assignment, and the exposure surface are gone; the
  // component is the same one testing-first markets render mid-page. PostHog
  // experiment 87989 concluded and its flag archived separately.

  // insurance-result-no-gate concluded on control (the email gate). The
  // no-gate arm (instant result, email recovered later) lost; the gate stays.
  // Experiment code + PostHog flag removed.

  // insurance-hero-social-proof (AHD-7850) concluded — the run was invalidated
  // by an exposure-recording race (treatment mis-attributed to control on
  // non-home pages, AHD-8246) so no clean winner was measured; the hero reverts
  // to control (current copy). Experiment code, EDGE_ASSIGNED entry, treatment
  // i18n keys, and the PostHog flag (85588) removed.

  // exit-intent-aggression concluded on control — the baked-in
  // DEFAULT_EXIT_INTENT_CONFIG thresholds (use-exit-intent.ts) won, and were
  // already the live defaults. The popup stays; the per-variant flag payload
  // override plumbing (parseExitIntentPayload + configOverrides) was removed.
  // PostHog flag archived separately.

  // quiz-result-membership concluded on membership-default, which shipped as
  // the permanent quiz-focus result layout for blood-only CH recommendations
  // (the membership-vs-one-off side-by-side). The flag, its edge-assignment,
  // and the exposure surface are gone; the layout is now gated purely in code
  // by `isQuizMembershipEligible` (blood_only + CH). PostHog flag archived
  // separately. Teardown grep: `git grep quiz-result-membership`.

  // main-cta-copy-v1 (MAIN_CTA_COPY_EXP) concluded on start-with-checkup, which
  // shipped as the permanent main CTA copy for the membership-first markets
  // (CH + AT): "Book check-up" / "Check-up buchen" / "Réserver un check-up"
  // (`ui.mainCtaStartCheckup`), replacing the "Become a member" ask. It won
  // decisively on the navbar cta_click secondary (+185%, 99.96% chance-to-win)
  // and led on the primary booking_step (+12%, ~92%) with a clean
  // booking_complete guardrail. The copy is now gated purely in code by
  // `feature:membership-first-positioning` (lib/country-gates.ts) — NL/DE keep
  // their testing-first fallbacks. The flag, its edge-assignment, the exposure
  // hook (useMainCtaCopyVariant), and the CH allow-list are all gone. PostHog
  // experiment 87991 concluded separately.

  "test-detail-ella-v1": {
    flag: "test-detail-ella-v1",
    variants: ["control", "test"],
    description:
      "Under-fold AI-assistant section on test detail pages (/tests/v/[code]/[slug]). control = no change (page renders as-is). test = adds a full-width section after the questions/insurance content and before the testimonials, with an AI chat card wired to the /api/chat pipeline (scope fullbodymri on the full-body-MRI page, tests elsewhere) plus a 'talk to our team' hand-off. Not shown in DE (checkup phase) or waitlist-only markets — the mount is gated page-side. " +
      "Metric wiring: edge-assigned (EDGE_ASSIGNED_EXPERIMENTS) — the client SDK serves no live flags on this site, so the client-only read it shipped with resolved `undefined` for everyone and the section was pinned to control no matter what PostHog said (AHD-9499; the AHD-8246 family of failures). The assignment cookie short-circuits getFeatureFlag()'s auto-exposure, so EllaTestSection fires $feature_flag_called explicitly via useReportExperimentExposure — from the mounted surface, so the population is the test-detail visitors who could see either arm rather than every visitor the bootstrap would expose. " +
      "Registered DARK: control 100 / test 0, mirroring flag 235798. Ramping is a deliberate two-place change — this array is what the edge actually assigns, so the PostHog split must move with it.",
    pages: ["/tests/v/[code]/[slug]"],
    metric: "cta_click",
  },

  "chat-mobile-teaser-question-v1": {
    flag: "chat-mobile-teaser-question-v1",
    variants: ["control", "question"],
    description:
      "Mobile chat launcher teaser ([AHD-14003](https://linear.app/aheadhealth/issue/AHD-14003)). control = the generic greeting pill. question = one page-specific question that sends on tap. The arm is read WITHOUT exposure in chat-launcher.tsx and handed to the hosted widget loader as window.ellaSettings.mobileTeaser + experiment; the loader stamps it as prompt_variant on its teaser events. " +
      "Metric wiring: edge-assigned (EDGE_ASSIGNED_EXPERIMENTS), so the arm is in the assignment cookie on the first frame. Exposure is NOT fired from marketing-site: the chat loader captures $feature_flag_called itself when the MOBILE teaser actually shows, so the population is mobile teaser viewers rather than every visitor the bootstrap would expose. " +
      "Registered DARK: control 100 / question 0, mirroring PostHog experiment 100956 (flag 311691). Ramping is a deliberate two-place change — the edge assigns from EDGE_ASSIGNED_EXPERIMENTS (or CHAT_MOBILE_TEASER_QUESTION_PCT where set), so the PostHog split must move with it.",
    pages: ["all pages with the chat launcher (mobile)"],
    metric: "chat_message_sent",
  },

  "quiz-chat-v1": {
    flag: "quiz-chat-v1",
    variants: ["control", "chat"],
    description:
      "AI quiz advisor ([AHD-14882](https://linear.app/aheadhealth/issue/AHD-14882)). control = the /quiz-focus wizard. chat = the same questions, recommendation and booking cart as a conversation that opens into the AI assistant. Both arms live at /quiz-focus: proxy.ts rewrites the chat arm to the static /quiz-chat page, so every CTA and ad URL is unchanged. " +
      "Metric wiring: edge-assigned (EDGE_ASSIGNED_EXPERIMENTS); the arm comes from resolveExperimentAssignments in proxy.ts, the same function that writes the assignment cookie. Exposure is fired explicitly from each arm's mounted page via useReportExperimentExposure, so the population is quiz visitors only. Primary metric quiz_booking_click, fired by both arms with an `arm` property. " +
      'Lead capture differs by design: the email is a gate before the result in control and an optional offer after the plan in chat, so PostHog quiz_completed is not comparable across arms; compare on quiz_booking_click. The GTM quiz_completed (the Google Ads / GA4 conversion) means "shown a plan" in both arms: control sends it at the email gate just before the result, chat when the plan appears (source quiz_chat), never again on the email. ' +
      "Registered DARK: control 100 / chat 0. Ramping is a two-place change (this array or QUIZ_CHAT_PCT, plus the PostHog flag). Keep /quiz-focus working whichever arm wins: the glue kill switch redirects the chat arm to it.",
    pages: ["/quiz-focus"],
    metric: "quiz_booking_click",
  },

  // quiz-cta-destination concluded on internal_quiz (the in-site /quiz-focus
  // funnel beat the external get.aheadhealth.com quiz on soft conversion at
  // ~97% chance-to-win). The funnel shipped as the permanent quiz CTA target;
  // the A/B split, external fallback, and QUIZ_FOCUS_STATUS rollout gate are
  // all removed. PostHog flag archived separately.

  // marketing-navbar-v2 launched at 100% — simplified navbar (FAQ out of
  // primary nav, locale picker in footer, search trigger) is the baseline.
  //
  // marketing-navbar-v3 launched at 100% — locale picker now sits in the
  // navbar on desktop (mobile keeps it in the footer + slide-out), the
  // desktop search icon is dropped (Cmd-K + the mobile slide-out reach
  // search), and CH gets Membership + Insurance primary nav items, the
  // Pricing/Contact-us desktop hide, and the "Check-ups" rename. See
  // useLocalizedNavigation + Navbar for the hardcoded behaviour.
  //
  // marketing-navbar-v4 (CH experiment) concluded on control. Its two cheap
  // wins — the desktop search icon (feature:nav-search) and the person-icon
  // login (feature:nav-login) — shipped to the base CH nav. The Reviews-in /
  // FAQ-off-desktop bundle was dropped: it underperformed and skewed the
  // experiment metric (idle-deferred exposure + fail-open-to-control
  // assignment). v2/v3/v4 PostHog flags are archived.
} as const satisfies Record<string, ExperimentConfig>

export type ExperimentKey = keyof typeof EXPERIMENTS

/**
 * Experiments the edge assigns deterministically so their client-read surfaces
 * paint the right arm on the FIRST frame instead of flashing control until
 * PostHog's idle-deferred SDK
 * loads. `proxy.ts` buckets each of these from the `ahead-experiment-id` cookie
 * (via `lib/experiment-bucketing.ts`) and writes the result into the
 * `ahead-experiment-assignments` cookie; the client reads it synchronously and
 * bootstraps PostHog with it.
 *
 * ⚠️ SOURCE-OF-TRUTH CONTRACT: `variants` (order + rollout) MUST mirror the
 * PostHog flag's variant config. The edge computes the split from THESE numbers,
 * not from PostHog at request time, so changing the rollout in the PostHog UI
 * does NOT propagate until this is updated + deployed. The parity test
 * (`__tests__/experiment-bucketing-parity.test.ts`) guards the algorithm, not
 * these numbers. Treat the code as the source of truth for these flags.
 *
 * The one exception is `splitEnvVar` (below), which lets ops move a two-arm
 * split from the Vercel dashboard instead of through a PR. Where it is set, the
 * ENV VAR is the source of truth and the numbers here are only the default.
 *
 * Only list condition-free multivariate flags here — the bucketer does rollout %
 * + variant split, NOT property/cohort targeting. A targeted flag added here
 * would be assigned to everyone.
 */
export const EDGE_ASSIGNED_EXPERIMENTS: readonly {
  flag: string
  /** Overall flag rollout %, 0–100. */
  rollout: number
  variants: readonly { key: string; rollout: number }[]
  /**
   * Optional env var that overrides this flag's split at request time, so the
   * percentage can be moved from the Vercel dashboard + a redeploy rather than a
   * code change, review and merge. Holds the TREATMENT share as an integer
   * 0–100; `control` takes the remainder.
   *
   * Only valid on a two-variant `[control, <treatment>]` entry — with three arms
   * a single number doesn't describe a split. `resolveEdgeVariants` enforces
   * that and falls back to the code default if it's ever violated.
   */
  splitEnvVar?: string
}[] = [
  // test-detail-ella-v1 — the under-fold Ella assistant section. Edge-assigned
  // for the same reason as the entries above: the client SDK serves no live
  // flags here, so the client-only read it shipped with (PR #2115) resolved
  // `undefined` for every visitor — the `test` arm was unreachable and the run
  // would have banked nothing but `None` exposures (AHD-9499).
  //
  // ⚠️ **This array — not PostHog — is what the edge actually assigns.** Listed
  // DARK (`test` 0%) so deploying this changes nothing for real traffic; it only
  // makes the arm *assignable*. Ramping means moving the split HERE and on flag
  // 235798 together, or the results UI describes a split that isn't running.
  //
  // Overall `rollout` stays 100 with the split carried by the variants —
  // out-of-rollout visitors collapse into `control` above, mixing un-bucketed
  // traffic into the control arm.
  //
  // Condition-free, as this list requires: no property/cohort targeting on the
  // PostHog flag. Who can SEE the section is gated in code (page.tsx mounts it
  // only outside DE and outside waitlist-only markets), and only that mounted
  // surface reports an exposure — see EllaTestSection.
  {
    flag: EXPERIMENTS["test-detail-ella-v1"].flag,
    rollout: 100,
    variants: [
      { key: "control", rollout: 100 },
      { key: "test", rollout: 0 },
    ],
  },
  // chat-mobile-teaser-question-v1 — the mobile chat teaser: page question vs
  // generic greeting. Edge-assigned so ChatLauncher can hand the arm to the
  // hosted loader at first injection (it reads window.ellaSettings once).
  //
  // ⚠️ **This array — not PostHog — is what the edge actually assigns.** Listed
  // DARK (`question` 0%): deploying this changes nothing for real traffic, it
  // only makes the arm *assignable*. Ramping is a two-place change: move the
  // split and the PostHog flag (311691, experiment 100956) together, or the
  // results UI describes a split that isn't running.
  //
  // `splitEnvVar`: set CHAT_MOBILE_TEASER_QUESTION_PCT in Vercel to the
  // `question` share (integer 0–100) and redeploy to ramp without a PR. Unset
  // falls back to the dark split below. The chat widget must be on a release
  // that knows `mobileTeaser` first — an older widget shows the greeting in both
  // arms and reports no exposure.
  //
  // Exposure is fired by the chat loader (customer-support) when the MOBILE
  // teaser shows, not by marketing-site: ChatLauncher reads the arm with
  // `useExperimentVariantNoExposure`, so the population is mobile teaser
  // viewers. Condition-free, as this list requires.
  {
    flag: EXPERIMENTS["chat-mobile-teaser-question-v1"].flag,
    rollout: 100,
    variants: [
      { key: "control", rollout: 100 },
      { key: "question", rollout: 0 },
    ],
    splitEnvVar: "CHAT_MOBILE_TEASER_QUESTION_PCT",
  },
  // quiz-chat-v1 — the AI quiz advisor. DARK (`chat` 0%): deploying it changes
  // nothing for real traffic. proxy.ts serves /quiz-chat under /quiz-focus for
  // the `chat` arm.
  {
    flag: EXPERIMENTS["quiz-chat-v1"].flag,
    rollout: 100,
    variants: [
      { key: "control", rollout: 100 },
      { key: "chat", rollout: 0 },
    ],
    splitEnvVar: "QUIZ_CHAT_PCT",
  },
]

/** The quiz-chat experiment flag and the arm that is served `/quiz-chat`. */
export const QUIZ_CHAT_FLAG = EXPERIMENTS["quiz-chat-v1"].flag
export const QUIZ_CHAT_ARM = "chat"
/** The two quiz arms, as every quiz event's `arm` property names them. */
export type QuizArm = "control" | typeof QUIZ_CHAT_ARM
/**
 * Session cookie set by the team preview (`/quiz-focus?quiz=chat|classic`,
 * see `proxy.ts`). While it is set, neither quiz page reports an exposure, so
 * team testing never lands in the experiment.
 */
export const QUIZ_PREVIEW_COOKIE = "ahead-quiz-preview"

/**
 * The split the edge should actually bucket `exp` with: the `splitEnvVar`
 * override where one is set and valid, otherwise the entry's own `variants`.
 *
 * Deliberately does NOT throw on a bad value. This runs in `proxy.ts`, on every
 * request — a throw here is a 500 on every page of the site, so a typo in the
 * Vercel dashboard would take marketing down. It falls back to the committed
 * default and shouts into the runtime log instead, which is loud without being
 * load-bearing. The validation that genuinely fails the build lives in
 * `lib/release-flags.ts`, where the blast radius is a deploy rather than prod.
 */
export function resolveEdgeVariants(
  exp: (typeof EDGE_ASSIGNED_EXPERIMENTS)[number]
): readonly { key: string; rollout: number }[] {
  if (!exp.splitEnvVar) return exp.variants

  // This module is in the CLIENT bundle too (`components/analytics/experiment`
  // imports EXPERIMENTS from it), and the lookup below is a dynamic
  // `process.env[key]` — which Next.js does NOT statically replace the way it
  // does a literal `process.env.FOO`, so it survives into client code where
  // `process` can be undefined. Only `proxy.ts` calls this today; the guard is
  // here so that if a component ever does, it degrades to the committed split
  // instead of throwing a ReferenceError and blanking the page.
  if (typeof process === "undefined" || !process.env) {
    console.error(
      `[experiments] resolveEdgeVariants(${exp.flag}) called without process.env ` +
        `— this is edge/server-only. Falling back to the committed split.`
    )
    return exp.variants
  }

  const raw = process.env[exp.splitEnvVar]?.trim()
  // Trimmed BEFORE the emptiness check on purpose: `Number("  ")` is 0, not
  // NaN, so a whitespace-only value would otherwise sail through validation and
  // read as a deliberate 0% — silently switching the experiment off.
  if (raw === undefined || raw === "") return exp.variants

  const pct = Number(raw)
  if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
    console.error(
      `[experiments] ${exp.splitEnvVar}="${raw}" must be an integer 0–100; ` +
        `falling back to the committed split for ${exp.flag}.`
    )
    return exp.variants
  }

  // A single percentage only describes a split when there are exactly two arms.
  if (exp.variants.length !== 2 || exp.variants[0].key !== "control") {
    console.error(
      `[experiments] ${exp.splitEnvVar} is set for ${exp.flag}, which is not a ` +
        `two-variant [control, treatment] flag; falling back to the committed split.`
    )
    return exp.variants
  }

  return [
    { key: "control", rollout: 100 - pct },
    { key: exp.variants[1].key, rollout: pct },
  ]
}

/**
 * Whether an edge experiment serves nothing but control right now: every
 * non-control arm of its effective split (after any `splitEnvVar`) is at 0%.
 * Such an experiment is kept OUT of the sticky assignment cookie, so the
 * visitors who come by while it is dark are bucketed afresh once it ramps
 * instead of being locked into control. Its readers default to control.
 */
export function isDarkEdgeExperiment(
  exp: (typeof EDGE_ASSIGNED_EXPERIMENTS)[number]
): boolean {
  return resolveEdgeVariants(exp).every(
    (v) => v.key === "control" || v.rollout === 0
  )
}

// Release flags (kill switches / content gates) live in `lib/release-flags.ts`
// and are now backed by Vercel env vars rather than PostHog. PostHog is
// reserved for experiments (the registry above).
export type { ReleaseFlagKey } from "@/lib/release-flags"
